/**
 * Screen 17 «Настройки» (salon-mockup.html settings). The language is saved on
 * the server (doc 07 §1). Reminders and channels are Phase 2 messaging:
 * reminders are hidden until then (D-66); channels are drawn exactly as the
 * mockup shows them, faded and not tappable (D-57). «Салон» rows carry live
 * counts.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth';
import { AppBar, Shell } from '../components/Shell';
import { ApiError, api } from '../core/api';
import { resolveAppVersion } from '../core/version';
import { errorText } from '../i18n';
import type { DayHours, Locale } from '../types';
import { useTimeOff } from './TimeOff';

/** «09–18»: the week's earliest opening to its latest closing. */
function hoursSummary(week: DayHours[]): string {
  if (!week.length) return '—';
  const h = (t: string) => (t.slice(3, 5) === '00' ? t.slice(0, 2) : t.slice(0, 5));
  const opens = week.map((d) => d.opens_at).sort()[0];
  const closes = week.map((d) => d.closes_at).sort().at(-1)!;
  return `${h(opens)}–${h(closes)}`;
}

function Off({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <div className="si" aria-disabled="true" style={{ opacity: 0.55 }}>
      {children}
      <span className={`sw-t${on ? '' : ' off'}`} role="switch" aria-checked={on} aria-disabled="true" />
    </div>
  );
}

export function SettingsScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  // The iPhone build at the bottom (D-71); null in a browser hides the line.
  const [appVersion, setAppVersion] = useState<string | null>(null);
  useEffect(() => {
    void resolveAppVersion().then(setAppVersion);
  }, []);
  const navigate = useNavigate();
  const { user, signedIn, signOut } = useAuth();
  const services = useQuery({ queryKey: ['services'], queryFn: api.services });
  const schedule = useQuery({ queryKey: ['schedule'], queryFn: api.schedule });
  const timeOff = useTimeOff();
  const [error, setError] = useState<string | null>(null);

  const choose = async (next: Locale) => {
    if (next === locale) return;
    setError(null);
    try {
      signedIn(await api.updateMe({ locale: next })); // the account's language wins (doc 07 §1)
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    }
  };

  return (
    <Shell active="settings">
      <AppBar title={t('settings.title')} />
      <div className="content">
        <div className="pane">
          {error && <div className="warnbox form-error" role="alert">{error}</div>}
          {user && (
            <>
              <span className="cap">{t('settings.account')}</span>
              <div className="seclist" data-go="account">
                <div className="acct">
                  <span className="ava" aria-hidden="true">{user.name.trim().charAt(0).toUpperCase()}</span>
                  <span className="grow">
                    <span className="nm">{user.name}</span>
                    <span className="em">{user.email}</span>
                  </span>
                </div>
              </div>
            </>
          )}
          <span className="cap">{t('settings.language')}</span>
          <div className="seclist">
            <div className="si">
              <span className="grow">
                <b style={{ fontSize: 14 }}>{t('settings.interface')}</b>
              </span>
              <span className="chips">
                {(['ky', 'ru'] as Locale[]).map((l) => (
                  <button key={l} type="button" className={`chip${locale === l ? ' on' : ''}`} aria-pressed={locale === l} onClick={() => void choose(l)}>
                    {t(`lang.${l}`)}
                  </button>
                ))}
              </span>
            </div>
          </div>

          <span className="cap">{t('settings.channels')}</span>
          <div className="seclist">
            {(
              [
                ['wa', 'WhatsApp', true],
                ['tg', 'Telegram', true],
                ['ig', 'Instagram', false],
                ['sms', 'SMS', false],
              ] as const
            ).map(([pill, name, on]) => (
              <Off key={pill} on={on}>
                <span className={`pill ${pill}`}>{name}</span>
                <span className="grow sm">{t(on ? 'settings.connected' : 'settings.notConnected')}</span>
              </Off>
            ))}
          </div>

          <span className="cap">{t('settings.salon')}</span>
          <div className="seclist">
            <div className="si" role="button" tabIndex={0} data-go="services" onClick={() => navigate('/settings/services')}>
              <span className="grow">
                <b style={{ fontSize: 14 }}>{t('settings.services')}</b>
              </span>
              <span className="xs">{services.data?.length ?? ''}</span>
            </div>
            <div className="si" role="button" tabIndex={0} data-go="schedule" onClick={() => navigate('/settings/schedule')}>
              <span className="grow">
                <b style={{ fontSize: 14 }}>{t('schedule.title')}</b>
              </span>
              <span className="xs tnum">{schedule.data ? hoursSummary(schedule.data) : ''}</span>
            </div>
            <div className="si" role="button" tabIndex={0} data-go="timeoff" onClick={() => navigate('/settings/time-off')}>
              <span className="grow">
                <b style={{ fontSize: 14 }}>{t('timeoff.title')}</b>
              </span>
              <span className="xs">{timeOff.data?.length ?? ''}</span>
            </div>
          </div>
          <button
            type="button"
            className="btn blk"
            style={{ color: 'var(--st-overlap-fg)', borderColor: 'var(--st-overlap-bd)' }}
            // The signed-in guard sends her to /login once the session is gone.
            onClick={() => void signOut()}
          >
            {t('app.signOut')}
          </button>
          {/* D-89: last on the screen, below «Выйти», so nobody taps it by accident. */}
          <div className="seclist" style={{ marginTop: 14 }}>
            <div className="si" role="button" tabIndex={0} data-go="accdel" onClick={() => navigate('/settings/delete-account')}>
              <span className="grow">
                <b style={{ fontSize: 14, color: 'var(--st-overlap-fg)' }}>{t('accountDelete.deleteAccount')}</b>
              </span>
              <span className="xs">›</span>
            </div>
          </div>
          {appVersion && (
            <p className="xs tnum" style={{ textAlign: 'center', marginTop: 14 }}>
              {t('settings.version', { version: appVersion })}
            </p>
          )}
        </div>
      </div>
    </Shell>
  );
}
